/**
 * Single source of truth for everything the site renders.
 * Mirrors Kailen_McCollum_Resume (current revision).
 *
 * NOTE: the "Custom Linux Router OS (Buildroot)" project and the Buildroot
 * skill entry are intentionally omitted from the site.
 */

export const profile = {
  name: 'Kailen McCollum',
  first: 'Kailen',
  last: 'McCollum',
  role: 'Network Technology',
  location: 'Ottawa, ON',
  timezone: 'UTC−5',
  email: 'McCollumKailen@gmail.com',
  github: 'https://github.com/Kailencodes',
  githubHandle: 'Kailencodes',
  linkedin: 'https://www.linkedin.com/in/kailen-mccollum-b08606230',
  resumeUrl: '/Kailen-McCollum-Resume-2026.pdf',
  available: 'Seeking a networking / IT infrastructure internship',
  tagline:
    'I build and break a segmented Cisco and Linux lab network, and a self-built ARM Kubernetes cluster — then automate the parts I got tired of doing by hand.',
  summary:
    'Third-year Network Technology student who builds and troubleshoots a segmented Cisco and Linux lab network and a self-built ARM Kubernetes cluster. Hands-on with VLANs, Layer 2 and Layer 3 routing and switching, subnetting, and packet analysis; writes Python, Bash, and C to automate configuration and testing. Currently working toward the Cisco CCNA.',
};

/* ---------------------------------------------------------------- education */

export const education = {
  school: 'Carleton University · Algonquin College',
  location: 'Ottawa, ON',
  degree: 'Bachelor of Information Technology',
  stream: 'Network Technology (BIT-NET)',
  window: 'Expected Winter 2029',
  coursework: [
    'Enterprise Networking',
    'Routing and Switching',
    'Computer Systems Foundations (ARM assembly, digital logic)',
    'Wireless Transmission Concepts',
    'Systems Programming',
  ],
};

export const certifications = [
  {
    name: 'Cisco Certified Network Associate',
    code: 'CCNA · Exam 200-301',
    status: 'In progress',
    live: true,
  },
];

export const activities = [
  'Member, Bachelor of Information Technology Society — Carleton University (2024 — Present)',
];

/* ------------------------------------------------------------------- skills */

export const skills = [
  {
    category: 'Networking',
    tone: 'blue',
    blurb: 'Layer 2 and Layer 3, wired and routed',
    items: [
      'TCP/IP', 'UDP', 'IPv4', 'IPv6', 'Subnetting', 'VLANs', '802.1Q trunking',
      'Inter-VLAN routing', 'L2 / L3 switching', 'STP / RSTP', 'EtherChannel / LACP',
      'ACLs', 'Static routing', 'OSPF', 'EIGRP', 'BGP', 'NAT', 'DHCP', 'DNS',
      'Port security', 'SNMP', 'Syslog', 'CDP / LLDP',
    ],
  },
  {
    category: 'Tools',
    tone: 'teal',
    blurb: 'What I have my hands on daily',
    items: [
      'Cisco IOS CLI', 'Wireshark', 'Cisco Packet Tracer', 'TFTP', 'SSH',
      'Proxmox', 'Git / GitHub', 'Network troubleshooting', 'Documentation',
    ],
  },
  {
    category: 'Systems',
    tone: 'indigo',
    blurb: 'Where the workloads actually run',
    items: [
      'Linux (Debian, Ubuntu)', 'Docker', 'Kubernetes (K3s)', 'Ansible',
      'CI/CD', 'Virtualization',
    ],
  },
  {
    category: 'Programming',
    tone: 'purple',
    blurb: 'Automation, tooling, and the web layer',
    items: [
      'Python', 'C', 'C++', 'Bash scripting', 'JavaScript', 'React.js', 'SQL', 'REST APIs',
    ],
  },
];

/* ----------------------------------------------------------------- projects */

export const projects = [
  {
    id: 'krakow',
    title: 'Segmented Multi-VLAN Lab Network',
    codename: 'Krakow',
    tone: 'blue',
    year: '2025 — Present',
    status: 'Live',
    featured: true,
    summary:
      'A three-VLAN segmented network on a Cisco WS-C3560G — Kubernetes, an isolated security lab, and out-of-band management, separated at Layer 2 and routed at Layer 3.',
    stack: ['Cisco IOS', '802.1Q', 'Wireshark', 'Proxmox', 'Linux Networking'],
    highlights: [
      'Designed and configured a three-VLAN segmented network on a Cisco WS-C3560G switch — VLAN 10 for the Kubernetes cluster, VLAN 20 for an isolated security lab, VLAN 99 for out-of-band management — with 802.1Q trunking and inter-VLAN routing.',
      'Upgraded switch firmware from Cisco IOS 12.2 to 15.0(2)SE11 over TFTP, then troubleshot and resolved SSH legacy cipher negotiation failures between modern clients and the older platform, restoring encrypted remote management.',
      'Configured EtherChannel link aggregation, port security, IPv4 and IPv6 addressing, NAT, and DHCP across Cisco IOS and Linux hosts; captured live traffic in Wireshark to verify routing behaviour and firewall policy.',
    ],
    // The one I'd talk about in an interview.
    warStory: {
      label: 'Debug log',
      title: 'SSH died after the IOS upgrade',
      body:
        'Moving 12.2 → 15.0(2)SE11 over TFTP went clean, but modern SSH clients then refused to negotiate with the switch — the platform only offered legacy ciphers and key exchange that current OpenSSH drops by default. Tracked it down from the client-side negotiation failure, matched the offered algorithms against what the platform actually supports, and restored encrypted remote management instead of falling back to Telnet.',
    },
    github: 'https://github.com/Kailencodes',
  },
  {
    id: 'k3s',
    title: 'ARM Kubernetes Cluster & Ansible Provisioning',
    codename: null,
    tone: 'teal',
    year: '2025 — Present',
    status: 'Live',
    featured: true,
    summary:
      'A K3s cluster built from bare metal on Orange Pi ARM64 boards, where a wiped node bootstraps and rejoins the cluster with no manual steps.',
    stack: ['Python', 'Bash', 'K3s', 'Ansible', 'Docker', 'CI/CD'],
    highlights: [
      'Built a K3s cluster from bare metal on Orange Pi ARM64 boards — flashed Debian Bookworm images, then wrote Ansible playbooks so a wiped node bootstraps and rejoins the cluster with no manual steps.',
      'Scaled from a single node to a multi-node cluster running containerized workloads, including scheduled network scanners reporting host and service inventory back to the lab.',
      'Wrote Python and Bash automation for deployment and health checks, version-controlled in Git with CI on each change.',
    ],
    warStory: {
      label: 'Design note',
      title: 'Rebuild, do not repair',
      body:
        'Every manual fix on a node is state nobody else can reproduce. The playbooks are written so the cheapest response to a broken node is wiping it — flash, bootstrap, rejoin, no hand-editing. Recovery becomes a rerun instead of an investigation.',
    },
    github: 'https://github.com/Kailencodes/orangepi-k3s-lab',
  },
  {
    id: 'replyflow',
    title: 'ReplyFlow',
    codename: 'Full-Stack SaaS',
    tone: 'purple',
    year: '2026',
    status: 'Shipped',
    featured: true,
    summary:
      'An AI-assisted review-response tool built and deployed end to end — front end, API, auth, database, and billing.',
    stack: ['React.js', 'Python', 'REST APIs', 'OAuth 2.0', 'PostgreSQL', 'Supabase', 'Stripe'],
    highlights: [
      'Built and deployed an AI-assisted review-response tool end to end — React front end, REST API backend, OAuth 2.0 authentication, PostgreSQL schema, and Stripe billing on Vercel, with documented backend services.',
      'Enabled local businesses to auto-generate context-aware, brand-appropriate responses to Google Business reviews at scale.',
      'Handled the full subscription lifecycle including Stripe webhook processing, and kept a clean, modular codebase with documentation for every backend service.',
    ],
    warStory: {
      label: 'Scope note',
      title: 'The whole lifecycle, not just the demo',
      body:
        'The interesting part was never generating text — it was everything around it: OAuth consent, a schema that survives a second customer, webhooks that stay correct when Stripe retries, and deploys that do not require me to be awake.',
    },
    github: 'https://github.com/Kailencodes',
  },
  {
    id: 'portscanner',
    title: 'Python Port Scanner',
    codename: null,
    tone: 'green',
    year: '2025',
    status: 'Repo',
    featured: false,
    summary:
      'Socket-level scanner for discovering hosts and services on the lab network. Runs as one of the scheduled workloads on the K3s cluster.',
    stack: ['Python', 'Socket programming', 'Network security'],
    highlights: [
      'Scans target hosts for open ports and reports service inventory back to the lab.',
      'Deployed as a containerized, scheduled workload on the Krakow K3s cluster.',
    ],
    warStory: null,
    github: 'https://github.com/Kailencodes/Python-Port-Scanner',
  },
];

/* --------------------------------------------------------------- experience */

export const experience = [
  {
    role: 'Sales Associate',
    org: 'Staples',
    location: 'Ottawa, ON',
    window: '2026 — Present',
    current: true,
    points: [
      'Translate technical specifications for computers, networking hardware, and peripherals into plain recommendations for non-technical customers, diagnosing the real need behind the request.',
      'Hold service standards under high-volume, fast-changing conditions across retail and hybrid service duties.',
    ],
  },
  {
    role: 'Front of House Coordinator',
    org: 'The Mill Café',
    location: 'Thornbury, ON',
    window: '2021 — 2024',
    current: false,
    points: [
      'Coordinated across kitchen, service, and management to resolve operational problems in real time through peak season.',
      'Maintained consistent service standards and communicated effectively with management and stakeholders.',
    ],
  },
];

/* --------------------------------------------------- lab topology (Krakow) */
/*
 * Drives the interactive diagram on the home page.
 * VLAN IDs and roles are from the résumé. The addressing below is
 * conventional RFC 1918 for illustration — swap in the real subnets
 * from the lab if you want the diagram to match it exactly.
 */

export const topology = {
  name: 'Krakow',
  platform: 'Cisco WS-C3560G',
  ios: '15.0(2)SE11',
  nodes: {
    wan: {
      id: 'wan',
      label: 'WAN',
      kind: 'cloud',
      caption: 'Upstream',
      tone: 'slate',
      detail: {
        title: 'Upstream / WAN',
        facts: [
          ['Egress', 'NAT overload to a single public address'],
          ['Addressing', 'Dual-stack IPv4 and IPv6'],
        ],
        note: 'Everything below this line is mine. Everything above it is not.',
      },
    },
    edge: {
      id: 'edge',
      label: 'Edge Router',
      kind: 'router',
      caption: 'NAT · DHCP · DNS',
      tone: 'indigo',
      detail: {
        title: 'Edge router',
        facts: [
          ['Services', 'NAT, DHCP, DNS forwarding'],
          ['Stack', 'IPv4 and IPv6 addressing'],
          ['Policy', 'Firewall policy between the lab and upstream'],
        ],
        note: 'Default gateway for the routed VLANs, and the only path out.',
      },
    },
    sw: {
      id: 'sw',
      label: 'WS-C3560G',
      kind: 'switch',
      caption: '802.1Q · inter-VLAN routing',
      tone: 'blue',
      detail: {
        title: 'Cisco WS-C3560G',
        facts: [
          ['Software', 'IOS 15.0(2)SE11 (upgraded from 12.2 over TFTP)'],
          ['Segmentation', '802.1Q trunking, three VLANs'],
          ['Routing', 'Inter-VLAN routing on switched virtual interfaces'],
          ['Aggregation', 'EtherChannel / LACP to the virtualization host'],
          ['Hardening', 'Port security, SSH replacing Telnet'],
        ],
        note: 'The core of the lab. Every design decision below shows up here first.',
      },
    },
    v10: {
      id: 'v10',
      label: 'VLAN 10',
      kind: 'vlan',
      caption: 'Kubernetes',
      subnet: '10.0.10.0/24',
      tone: 'teal',
      hosts: ['k3s-server', 'k3s-agent-01', 'k3s-agent-02'],
      detail: {
        title: 'VLAN 10 — Kubernetes cluster',
        facts: [
          ['Purpose', 'K3s control plane and agents on Orange Pi ARM64'],
          ['Uplink', 'EtherChannel / LACP bundle to the switch'],
          ['Provisioning', 'Ansible — a wiped node rejoins with no manual steps'],
          ['Workloads', 'Containerized services and scheduled network scanners'],
        ],
        note: 'Production-shaped: the cluster gets its own broadcast domain, not a corner of a flat network.',
      },
    },
    v20: {
      id: 'v20',
      label: 'VLAN 20',
      kind: 'vlan',
      caption: 'Security lab',
      subnet: '10.0.20.0/24',
      tone: 'purple',
      hosts: ['proxmox-lab', 'target-01', 'target-02'],
      detail: {
        title: 'VLAN 20 — Isolated security lab',
        facts: [
          ['Purpose', 'Deliberately vulnerable targets, kept away from everything else'],
          ['Isolation', 'Layer 2 separation plus ACLs on the routed interface'],
          ['Host', 'Proxmox virtualization for disposable targets'],
          ['Verification', 'Wireshark captures confirm the policy actually holds'],
        ],
        note: 'The point of segmentation is that a compromise here reaches nothing else.',
      },
    },
    v99: {
      id: 'v99',
      label: 'VLAN 99',
      kind: 'vlan',
      caption: 'Management',
      subnet: '10.0.99.0/24',
      tone: 'blue',
      hosts: ['mgmt-jump', 'tftp', 'syslog'],
      detail: {
        title: 'VLAN 99 — Out-of-band management',
        facts: [
          ['Purpose', 'Device management separated from user and workload traffic'],
          ['Access', 'SSH only — Telnet removed after the cipher fix'],
          ['Services', 'TFTP for image transfers, Syslog collection'],
          ['Discovery', 'CDP / LLDP for topology verification'],
        ],
        note: 'If management shares a VLAN with workloads, losing the workload VLAN loses the switch too.',
      },
    },
  },
  links: [
    { from: 'wan', to: 'edge', label: null, kind: 'plain' },
    { from: 'edge', to: 'sw', label: '802.1Q trunk', kind: 'trunk' },
    { from: 'sw', to: 'v10', label: 'LACP', kind: 'lacp' },
    { from: 'sw', to: 'v20', label: null, kind: 'access' },
    { from: 'sw', to: 'v99', label: null, kind: 'access' },
  ],
};

/* -------------------------------------------------------------- stat strip */

export const stats = [
  { value: '3', label: 'VLANs segmented', sub: 'Layer 2 isolation, Layer 3 routed' },
  { value: 'ARM64', label: 'K3s cluster', sub: 'Bare metal to multi-node' },
  { value: 'CCNA', label: 'In progress', sub: 'Exam 200-301' },
  { value: '2029', label: 'BIT-NET', sub: 'Carleton · Algonquin' },
];
